import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import { uniq } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { makeInventory } from '../../api/store/makeInventory';
import { Store } from '../../apiTypes';
import AddIngredientButton from '../../components/AddIngredientButton';
import Image from '../../components/Image';
import PrimaryButton from '../../components/PrimaryButton';
import QntInput from '../../components/QntInput';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import { getComponentCost } from '../../utils/getPriceByUnit';
import getQntLabel from '../../utils/getQntLabel';
import useMedia from '../../utils/useMedia';

type Props = {
  store: DbDocument<Store>;
  onClose: () => void;
};

const Inventory = ({ store, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const { ingredients, worker, productsMap, semiProductsMap, ingredientsMap } = useCafe();
  const { isMobile } = useMedia();
  const [residues, setResidues] = useState(store.residues);
  const [enablePacking, setEnablePacking] = useState(true);
  const [autoFocus, setAutoFocus] = useState('');

  const costDiffMap = useMemo(
    () =>
      Object.entries(residues).reduce<Record<string, { diff: number; cost: number }>>((acc, [id, qnt]) => {
        const diff = (qnt || 0) - (store.residues[id] || 0);
        const ingredient = ingredientsMap[id];
        const cost = ingredient ? getComponentCost({ ...ingredient, qnt: diff }, productsMap, semiProductsMap) : 0;

        return { ...acc, [id]: { diff, cost } };
      }, {}),
    [ingredientsMap, productsMap, residues, semiProductsMap, store.residues]
  );

  const totalCost = useMemo(() => Object.values(costDiffMap).reduce((acc, i) => acc + i.cost, 0), [costDiffMap]);

  const rows = useMemo(
    () =>
      ingredients
        .filter((i) => residues[i.id] !== undefined)
        .map((i) => ({
          ...i,
          qnt: residues[i.id],
        })),
    [ingredients, residues]
  );

  const categories = useMemo(() => uniq(rows.map((r) => r.category)), [rows]);

  const rowsByCategory = useMemo(
    () => categories.map((c) => ({ name: c, items: rows.filter((r) => r.category === c) })),
    [categories, rows]
  );

  const filteredOptions = useMemo(
    () => ingredients.filter((i) => residues[i.id] === undefined),
    [ingredients, residues]
  );

  const saveChanges = async () => {
    if (worker) {
      const costDiff = Object.fromEntries(Object.entries(costDiffMap).map(([id, i]) => [id, i.cost]));
      await makeInventory({ cafeSlug, store, newResidues: residues, costDiff, worker });
      onClose();
    }
  };

  return (
    <Dialog onClose={onClose} open fullWidth fullScreen={isMobile} maxWidth="md">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {`${t.stock_inventory[lang]}: ${store?.name}`}
          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'right', mb: 3 }}>
          <FormControlLabel
            control={
              <Switch size="small" checked={enablePacking} onChange={(e) => setEnablePacking(e.target.checked)} />
            }
            label={t.display_packaging[lang]}
          />
        </Box>

        {!isMobile && (
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <Typography>{t.name[lang]}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography>{t.quantity[lang]}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography>{t.valid_quantity[lang]}</Typography>
            </Grid>
            <Grid item xs={1}>
              <Typography>{t.difference[lang]}</Typography>
            </Grid>
          </Grid>
        )}
        {rowsByCategory.map((category) => (
          <Fragment key={category.name || 'uncategorized'}>
            <Typography variant="subtitle2" sx={{ mt: 2, color: (t) => t.palette.text.secondary }}>
              {category.name || t.uncategorized[lang]}
            </Typography>

            {category.items.map((i) => {
              const diff = costDiffMap[i.id]?.diff || 0;
              const cost = costDiffMap[i.id]?.cost || 0;
              return (
                <Grid
                  key={i.id}
                  container
                  alignItems="center"
                  sx={{ p: 1, borderRadius: 1, ':hover': { bgcolor: (t) => t.palette.background.default } }}
                >
                  <Grid item xs={5} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Image src={i.imageUrl} />
                    <Box sx={{ ml: 1 }}>
                      <Typography>{i.name}</Typography>
                      {i.qpp && (
                        <Typography sx={{ color: (t) => t.palette.text.secondary, fontSize: 12 }}>
                          {i.qpp ? `${i.qpp}${t[i.unit][lang]} ${t.in[lang]} ${t.pac[lang]}` : t[i.unit][lang]}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={2} sx={{ pl: 1 }}>
                    <Typography sx={{ color: (t) => (diff ? t.palette.error.main : undefined) }}>
                      {getQntLabel(store.residues[i.id], i, lang, !enablePacking)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4.5} sx={{ pl: isMobile ? 0 : 1, mt: isMobile ? 1 : 0 }}>
                    <QntInput
                      ingredient={i}
                      qnt={i.qnt || 0}
                      onChange={(val) => setResidues({ ...residues, [i.id]: val })}
                      disablePacking={!enablePacking}
                      autoFocus={i.id === autoFocus}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1.5} sx={{ pl: isMobile ? 0 : 1, mt: isMobile ? 1 : 0, textAlign: 'right' }}>
                    {!!diff && (
                      <Typography
                        variant="body2"
                        color={(t) => (diff > 0 ? t.palette.success.main : t.palette.error.main)}
                      >
                        {diff > 0 ? '+' : ''}
                        {getQntLabel(diff, i, lang)}
                      </Typography>
                    )}
                    {!!cost && (
                      <Typography
                        variant="body2"
                        color={(t) => (diff > 0 ? t.palette.success.main : t.palette.error.main)}
                      >
                        {diff > 0 ? '+' : ''}
                        {currency(cost, cafe?.currency, true)}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              );
            })}
          </Fragment>
        ))}

        <Box sx={{ maxWidth: 260, my: 0.5 }}>
          <AddIngredientButton
            options={filteredOptions}
            onAdd={(value) => {
              setAutoFocus(value.id);
              setResidues({
                ...residues,
                [value.id]: 0,
              });
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{ textAlign: 'right', color: (t) => (totalCost >= 0 ? t.palette.success.main : t.palette.error.main) }}
        >
          {t.total[lang]}: {totalCost > 0 ? '+' : ''}
          {currency(totalCost, cafe?.currency)}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <PrimaryButton type="submit" variant="contained" onClick={saveChanges}>
          {t.take_inventory[lang]}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default Inventory;
